import { useQuery } from '@tanstack/react-query';
import { endpoints, getIDFromUrl, withSideload } from '@rossum/api-client';
import { CustomRelation } from '@rossum/api-client/relations';
import { api } from '../../../lib/apiClient';
import { useTransformAnnotations } from '../../document-list-columns/hooks/useTransformAnnotation';
import { visibleStatuses } from '../../document-list-columns/status/constants';

const CUSTOM_RELATION_MEMBERS_QUERY_KEY = 'custom-relation-members';

export const useFetchCustomRelationMembers = (
  relation: CustomRelation | null
) => {
  const memberIds = relation
    ? [
        ...(relation.parent ? [relation.parent] : []),
        ...relation.annotations,
      ].map(getIDFromUrl)
    : [];

  const {
    data,
    isInitialLoading: isFetching,
    isError,
  } = useQuery({
    queryKey: [CUSTOM_RELATION_MEMBERS_QUERY_KEY, ...memberIds],
    queryFn: () =>
      api.request(
        withSideload(
          endpoints.annotations.list({
            pageSize: memberIds.length,
            id: memberIds,
            status: visibleStatuses,
          }),
          { documents: true, modifiers: true, relations: true }
        )
      ),
    enabled: memberIds.length > 0,
    keepPreviousData: true,
  });

  const { transformedAnnotations, isLoading: isTransforming } =
    useTransformAnnotations({
      annotations: data?.results,
      documents: data?.documents,
      modifiers: data?.modifiers,
      relations: data?.relations,
    });

  const members = transformedAnnotations ?? [];

  // The parent might not be accessible (e.g. purged or restricted).
  const parent = members.find(({ url }) => url === relation?.parent) ?? null;

  const children = members.filter(({ url }) =>
    relation?.annotations.includes(url)
  );

  return {
    parent,
    children,
    isLoading: isFetching || isTransforming,
    isError,
  };
};
